import {
    OfflineCacheManifest,
    OfflineCacheManifestItem,
    getOfflineStorage,
    OfflineStorage
} from './storage';
import { getNetworkService, NetworkService } from './network';
import $ from 'jquery';

type PrefetchListener = (progress: { current: number; total: number; label: string }) => void;

const CHECKLIST_RESPONSE_PREFIX = 'checklist_response:';
const CHECKLIST_RESULT_PREFIX = 'checklist_result:';
const CHECKLIST_MAPPING_PREFIX = 'checklist_mapping_response:';
const INSPECTION_VIEWER_OVERVIEW_PREFIX = 'inspection_viewer_overview:';

export class PrefetchService {
    private storage: OfflineStorage;
    private network: NetworkService;
    private _baseuri: string = '/wiz';
    private _listeners: PrefetchListener[] = [];

    constructor() {
        this.storage = getOfflineStorage();
        this.network = getNetworkService();
    }

    setBaseUri(uri: string) {
        this._baseuri = uri;
    }

    private getServerOrigin(): string {
        if (/^https?:\/\//.test(this._baseuri)) {
            try {
                return new URL(this._baseuri).origin;
            } catch (error) {
                return '';
            }
        }
        return '';
    }

    private resolveServerUrl(path: string): string {
        const normalized = String(path || '').trim();
        if (!normalized || /^https?:\/\//.test(normalized)) return normalized;
        const origin = this.getServerOrigin();
        if (!origin) return normalized;
        return normalized.startsWith('/') ? `${origin}${normalized}` : `${origin}/${normalized}`;
    }

    onProgress(listener: PrefetchListener): () => void {
        this._listeners.push(listener);
        return () => { this._listeners = this._listeners.filter(fn => fn !== listener); };
    }

    private notify(current: number, total: number, label: string) {
        this._listeners.forEach(fn => {
            try { fn({ current, total, label }); } catch (e) { }
        });
    }

    async prefetchProject(projectId: string): Promise<void> {
        if (!this.network.isOnline) {
            throw new Error('프리페치는 온라인 상태에서만 가능합니다.');
        }

        await this.storage.setCacheManifest(this.storage.getEmptyCacheManifest(projectId));

        const steps: { label: string; fn: () => Promise<void> }[] = [
            { label: '프로젝트/도면/시설물 데이터', fn: () => this.prefetchProjectData(projectId) },
            { label: '시설물 전경사진 및 도면 연결', fn: () => this.prefetchFacilityDetails(projectId) },
            { label: '손상/드로잉 데이터 및 손상 사진', fn: () => this.prefetchViewerData(projectId) },
            { label: '도면 PDF 파일', fn: () => this.prefetchDrawingPdfs(projectId) },
            { label: '체크리스트 항목/응답/사진 캐시', fn: () => this.prefetchChecklistData(projectId) }
        ];

        let currentStep = 0;
        for (const step of steps) {
            this.notify(currentStep, steps.length, step.label);
            try {
                await step.fn();
            } catch (e) {
                console.error(`[Prefetch] Step "${step.label}" failed:`, e);
            }
            currentStep++;
            this.notify(currentStep, steps.length, step.label);
        }

        const manifest = await this.storage.getCacheManifest(projectId);
        if (manifest) {
            await this.storage.setCacheManifest({
                ...manifest,
                completed_at: Date.now(),
                updated_at: Date.now()
            });
        }

        const completed = await this.storage.getCacheManifest(projectId);
        await this.storage.setMeta(`prefetch_${projectId}`, {
            completed_at: Date.now(),
            status: completed?.status === 'completed' ? 'completed' : 'partial',
            manifest: completed
        });
    }

    async isPrefetched(projectId: string): Promise<boolean> {
        const manifest = await this.storage.getCacheManifest(projectId);
        if (manifest) return manifest.status === 'completed';

        const meta = await this.storage.getMeta(`prefetch_${projectId}`);
        return meta && meta.status === 'completed';
    }

    async getManifest(projectId: string): Promise<OfflineCacheManifest | null> {
        return this.storage.getCacheManifest(projectId);
    }

    async getMissingItems(projectId: string, categories?: string[]): Promise<OfflineCacheManifestItem[]> {
        return this.storage.getCacheManifestMissingItems(projectId, categories);
    }

    private async prefetchProjectData(projectId: string): Promise<void> {
        try {
            const res: any = await this.apiCall('page.project.detail', 'detail', { id: projectId });
            if (!res || res.code !== 200 || !res.data) {
                throw new Error(res?.message || res?.data || '프로젝트 정보를 불러올 수 없습니다.');
            }

            const data = res.data;
            if (data.project) {
                await this.storage.put('projects', data.project);
                await this.cacheCurrentProjectSelection(data.project);
                await this.markCached(projectId, 'project_data', `project:${projectId}`, data.project.title || '프로젝트 정보', data.project, { ref_id: projectId });
            }

            if (Array.isArray(data.drawings)) {
                await this.storage.putMany('drawings', data.drawings);
                for (const drawing of data.drawings) {
                    await this.markCached(
                        projectId,
                        'drawing_data',
                        `drawing:${drawing.id}`,
                        drawing.filename || drawing.name || `도면 ${drawing.id}`,
                        drawing,
                        { ref_id: drawing.id, filename: drawing.filename || drawing.name || '' }
                    );
                }
            }

            if (Array.isArray(data.facilities)) {
                await this.storage.putMany('facilities', data.facilities);
                for (const facility of data.facilities) {
                    await this.markCached(
                        projectId,
                        'facility_data',
                        `facility:${facility.id}`,
                        facility.name || `시설물 ${facility.id}`,
                        facility,
                        { ref_id: facility.id }
                    );

                    if (Array.isArray(facility.drawings)) {
                        for (const drawing of facility.drawings) {
                            await this.cacheFacilityDrawingMapping(facility.id, drawing.id);
                        }
                    }
                }
            }
        } catch (e: any) {
            await this.markFailed(projectId, 'project_data', `project:${projectId}`, '프로젝트 정보', e);
        }
    }

    private async prefetchFacilityDetails(projectId: string): Promise<void> {
        const facilities = await this.storage.getAllByIndex('facilities', 'project_id', projectId);
        for (const facility of facilities) {
            try {
                const res: any = await this.apiCall('page.project.detail', 'get_facility_detail', {
                    id: facility.id,
                    facility_id: facility.id
                });
                if (!res || res.code !== 200 || !res.data) {
                    throw new Error(res?.message || res?.data || '시설물 상세 정보를 불러올 수 없습니다.');
                }

                const detail = res.data;
                const overviewPhotos = this.normalizePhotoList(detail.overview_photos || []);
                const nextFacility = {
                    ...facility,
                    ...(detail.facility || {}),
                    overviewPhotos
                };
                await this.storage.put('facilities', nextFacility);
                await this.storage.setMeta(`${INSPECTION_VIEWER_OVERVIEW_PREFIX}${facility.id}`, overviewPhotos);
                await this.markCached(projectId, 'facility_data', `facility:${facility.id}`, nextFacility.name || `시설물 ${facility.id}`, nextFacility, { ref_id: facility.id });

                if (Array.isArray(detail.drawings)) {
                    for (const drawing of detail.drawings) {
                        if (drawing && drawing.id) {
                            await this.storage.put('drawings', drawing);
                            await this.cacheFacilityDrawingMapping(facility.id, drawing.id);
                            await this.markCached(
                                projectId,
                                'drawing_data',
                                `drawing:${drawing.id}`,
                                drawing.filename || drawing.name || `도면 ${drawing.id}`,
                                drawing,
                                { ref_id: drawing.id, parent_id: facility.id, filename: drawing.filename || drawing.name || '' }
                            );
                        }
                    }
                }

                await this.recordPhotos(projectId, 'overview_photo', facility.id, overviewPhotos, '전경사진');
                await this.recordInspectionExportAvailability(projectId, facility.id, nextFacility.name || '시설물');
            } catch (e: any) {
                await this.markFailed(projectId, 'facility_data', `facility:${facility.id}`, facility.name || `시설물 ${facility.id}`, e, { ref_id: facility.id });
            }
        }
    }

    private async prefetchViewerData(projectId: string): Promise<void> {
        const facilities = await this.storage.getAllByIndex('facilities', 'project_id', projectId);

        for (const facility of facilities) {
            const fds = await this.storage.getAllByIndex('facility_drawings', 'facility_id', facility.id);
            for (const fd of fds) {
                try {
                    const res: any = await this.apiCall('page.inspection.viewer', 'get_viewer_data', {
                        facility_id: facility.id,
                        drawing_id: fd.drawing_id
                    });
                    if (!res || res.code !== 200 || !res.data) {
                        throw new Error(res?.message || res?.data || '도면 뷰어 데이터를 불러올 수 없습니다.');
                    }

                    const data = res.data;
                    if (data.facility && data.facility.id) await this.storage.put('facilities', data.facility);
                    if (data.drawing && data.drawing.id) await this.storage.put('drawings', data.drawing);
                    if (data.project && data.project.id) await this.storage.put('projects', data.project);
                    if (data.damages) {
                        await this.storage.put('damages', {
                            drawing_id: fd.drawing_id,
                            data: data.damages,
                            synced_at: Date.now()
                        });
                    }
                    if (data.drawings) {
                        await this.storage.put('annotations', {
                            drawing_id: fd.drawing_id,
                            data: data.drawings,
                            synced_at: Date.now()
                        });
                    }
                    if (Array.isArray(data.overview_photos)) {
                        await this.storage.setMeta(`${INSPECTION_VIEWER_OVERVIEW_PREFIX}${facility.id}`, data.overview_photos);
                        await this.recordPhotos(projectId, 'overview_photo', facility.id, data.overview_photos, '전경사진');
                    }
                    if (data.sync) await this.storage.setMeta(`inspection_viewer_sync:${fd.drawing_id}`, data.sync);

                    await this.markCached(
                        projectId,
                        'drawing_data',
                        `viewer:${facility.id}:${fd.drawing_id}`,
                        `${facility.name || '시설물'} 도면 작업 데이터`,
                        {
                            damages: data.damages || [],
                            drawings: data.drawings || [],
                            sync: data.sync || null
                        },
                        { ref_id: fd.drawing_id, parent_id: facility.id }
                    );
                    await this.recordDamagePhotos(projectId, fd.drawing_id, data.damages || []);
                } catch (e: any) {
                    await this.markFailed(
                        projectId,
                        'drawing_data',
                        `viewer:${facility.id}:${fd.drawing_id}`,
                        `${facility.name || '시설물'} 도면 작업 데이터`,
                        e,
                        { ref_id: fd.drawing_id, parent_id: facility.id }
                    );
                }
            }
        }
    }

    private async prefetchDrawingPdfs(projectId: string): Promise<void> {
        const drawings = await this.storage.getAllByIndex('drawings', 'project_id', projectId);
        for (const drawing of drawings) {
            const drawingId = drawing.id;
            const label = drawing.filename || drawing.name || `도면 PDF ${drawingId}`;
            const pdfUrl = this.resolveServerUrl(`/api/file/pdf?id=${encodeURIComponent(drawingId)}`);
            try {
                const existing = await this.storage.get('files', `pdf_${drawingId}`);
                if (existing && (existing.data || existing.blob)) {
                    await this.markCached(projectId, 'drawing_pdf', `pdf:${drawingId}`, label, existing, {
                        ref_id: drawingId,
                        url: pdfUrl,
                        filename: label,
                        size: existing.size || this.byteSize(existing.data || existing.blob)
                    });
                    continue;
                }

                const response = await fetch(pdfUrl, { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                await this.storage.put('files', {
                    id: `pdf_${drawingId}`,
                    type: 'pdf',
                    ref_id: drawingId,
                    filename: label,
                    mime_type: blob.type || 'application/pdf',
                    data: arrayBuffer,
                    size: arrayBuffer.byteLength,
                    cached_at: Date.now()
                });

                await this.cachePdfInServiceWorker(pdfUrl, arrayBuffer);
                await this.markCached(projectId, 'drawing_pdf', `pdf:${drawingId}`, label, arrayBuffer, {
                    ref_id: drawingId,
                    url: pdfUrl,
                    filename: label,
                    size: arrayBuffer.byteLength
                });
            } catch (e: any) {
                await this.markFailed(projectId, 'drawing_pdf', `pdf:${drawingId}`, label, e, {
                    ref_id: drawingId,
                    url: pdfUrl,
                    filename: label
                });
            }
        }
    }

    private async prefetchChecklistData(projectId: string): Promise<void> {
        try {
            const res: any = await this.apiCall('page.checklist.facilities', 'load', { project_id: projectId });
            if (!res || res.code !== 200 || !res.data) {
                throw new Error(res?.message || res?.data || '체크리스트 시설물 데이터를 불러올 수 없습니다.');
            }

            if (res.data.project) {
                await this.storage.put('projects', res.data.project);
                await this.markCached(projectId, 'project_data', `project:${projectId}`, res.data.project.title || '프로젝트 정보', res.data.project, { ref_id: projectId });
            }

            const facilities = Array.isArray(res.data.facilities) ? res.data.facilities : [];
            for (const facility of facilities) {
                facility.project_id = facility.project_id || projectId;
            }
            if (facilities.length > 0) await this.storage.putMany('facilities', facilities);

            for (const facility of facilities) {
                await this.markCached(projectId, 'facility_data', `facility:${facility.id}`, facility.name || `시설물 ${facility.id}`, facility, { ref_id: facility.id });
                const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
                for (const checklist of checklists) {
                    try {
                        await this.ensureChecklistResponse(projectId, facility, checklist);
                    } catch (e: any) {
                        await this.markFailed(
                            projectId,
                            'checklist_response',
                            `checklist_response:${checklist.mapping_id || checklist.checklist_id}`,
                            `${facility.name || '시설물'} ${checklist.title || '체크리스트'} 응답`,
                            e,
                            { ref_id: checklist.response_id || checklist.mapping_id || checklist.checklist_id, parent_id: facility.id }
                        );
                    }

                    if (checklist.mapping_id && checklist.response_id) {
                        await this.storage.setMeta(`${CHECKLIST_MAPPING_PREFIX}${checklist.mapping_id}`, {
                            response_id: checklist.response_id,
                            status: checklist.response_status || 'draft',
                            cached_at: Date.now()
                        });
                    }
                    if (checklist.response_id) {
                        await this.prefetchChecklistResponse(projectId, facility, checklist);
                    }
                }
            }
        } catch (e: any) {
            await this.markFailed(projectId, 'checklist_result', `checklist:list:${projectId}`, '체크리스트 목록', e, { ref_id: projectId });
        }
    }

    private async ensureChecklistResponse(projectId: string, facility: any, checklist: any): Promise<void> {
        if (!checklist || checklist.response_id || !checklist.mapping_id) return;

        const res: any = await this.apiCall('page.checklist.facilities', 'start_checklist', {
            mapping_id: checklist.mapping_id
        });
        if (!res || res.code !== 200 || !res.data?.response_id) {
            throw new Error(res?.message || res?.data || '체크리스트 응답을 준비할 수 없습니다.');
        }

        checklist.response_id = res.data.response_id;
        checklist.response_status = res.data.status || 'draft';
        await this.storage.setMeta(`${CHECKLIST_MAPPING_PREFIX}${checklist.mapping_id}`, {
            response_id: checklist.response_id,
            status: checklist.response_status,
            cached_at: Date.now()
        });

        const cachedFacility = await this.storage.get('facilities', facility.id) || facility;
        if (Array.isArray(cachedFacility.checklists)) {
            const target = cachedFacility.checklists.find((entry: any) => entry && entry.mapping_id === checklist.mapping_id);
            if (target) {
                target.response_id = checklist.response_id;
                target.response_status = checklist.response_status;
                await this.storage.put('facilities', cachedFacility);
            }
        }

        await this.markCached(
            projectId,
            'checklist_response',
            `checklist_response:${checklist.response_id}`,
            `${facility.name || '시설물'} ${checklist.title || '체크리스트'} 응답`,
            checklist,
            { ref_id: checklist.response_id, parent_id: facility.id }
        );
    }

    private async prefetchChecklistResponse(projectId: string, facility: any, checklist: any): Promise<void> {
        const responseId = checklist.response_id;
        const label = `${facility.name || '시설물'} ${checklist.title || '체크리스트'} 응답`;
        try {
            const res: any = await this.apiCall('page.checklist.respond', 'load_response', { response_id: responseId });
            if (!res || res.code !== 200 || !res.data) {
                throw new Error(res?.message || res?.data || '체크리스트 응답을 불러올 수 없습니다.');
            }

            await this.storage.setMeta(`${CHECKLIST_RESPONSE_PREFIX}${responseId}`, res.data);
            await this.storage.setMeta(`${CHECKLIST_RESULT_PREFIX}${responseId}`, res.data);
            if (res.data.response) {
                checklist.response_status = res.data.response.status || checklist.response_status || 'draft';
                checklist.response_id = responseId;
            }
            if (res.data.facility && res.data.facility.id) await this.mergeAndCacheFacility(res.data.facility);
            if (res.data.project && res.data.project.id) await this.storage.put('projects', res.data.project);

            await this.markCached(projectId, 'checklist_response', `checklist_response:${responseId}`, label, res.data, {
                ref_id: responseId,
                parent_id: facility.id
            });
            await this.markCached(projectId, 'checklist_result', `checklist:${responseId}`, `${label} 결과 캐시`, res.data, {
                ref_id: responseId,
                parent_id: facility.id
            });
            await this.recordChecklistItems(projectId, responseId, res.data.sections || []);
            await this.recordChecklistPhotos(projectId, responseId, res.data.answers || {});
            if (res.data.response?.status === 'submitted') {
                await this.recordChecklistExportAvailability(projectId, responseId, checklist, label);
            }
        } catch (e: any) {
            await this.markFailed(projectId, 'checklist_response', `checklist_response:${responseId}`, label, e, {
                ref_id: responseId,
                parent_id: facility.id
            });
        }
    }

    private async cacheFacilityDrawingMapping(facilityId: string, drawingId: string): Promise<void> {
        await this.storage.put('facility_drawings', {
            id: `${facilityId}_${drawingId}`,
            facility_id: facilityId,
            drawing_id: drawingId
        });
    }

    private async mergeAndCacheFacility(facility: any): Promise<void> {
        if (!facility || !facility.id) return;
        const existing = await this.storage.get('facilities', facility.id);
        const next = {
            ...(existing || {}),
            ...facility
        };
        if (!Array.isArray(next.checklists) && Array.isArray(existing?.checklists)) {
            next.checklists = existing.checklists;
        }
        if (!Array.isArray(next.overviewPhotos) && Array.isArray(existing?.overviewPhotos)) {
            next.overviewPhotos = existing.overviewPhotos;
        }
        await this.storage.put('facilities', next);
    }

    private async cacheCurrentProjectSelection(project: any): Promise<void> {
        const projectId = String(project?.id || project?.project_id || '').trim();
        if (!projectId) return;

        const user = await this.storage.get('auth', 'current_user');
        const recordId = user?.id && user.id !== 'current_user' ? user.id : '';
        const userId = String(user?.user_id || user?.login_id || recordId || '').trim();
        if (!userId) return;

        const normalizedProject = this.normalizeProjectPeriod(project);
        await this.storage.put('projects', normalizedProject);
        await this.storage.put('user_selections', {
            id: `${userId}_${projectId}`,
            user_id: userId,
            project_id: projectId,
            source: 'prefetch_project',
            cached_at: Date.now()
        });

        const metaKey = `selected_projects:${userId}`;
        const snapshot = await this.storage.getMeta(metaKey) || {};
        const projects = Array.isArray(snapshot.projects) ? [...snapshot.projects] : [];
        const index = projects.findIndex((entry: any) => String(entry?.id || entry?.project_id || '') === projectId);
        if (index >= 0) projects[index] = { ...projects[index], ...normalizedProject };
        else projects.push(normalizedProject);

        const projectIds = Array.from(new Set([
            ...(Array.isArray(snapshot.project_ids) ? snapshot.project_ids : []),
            projectId
        ].map((id: any) => String(id || '').trim()).filter((id: string) => !!id)));

        await this.storage.setMeta(metaKey, {
            user_id: userId,
            project_ids: projectIds,
            projects,
            cached_at: Date.now()
        });
    }

    private normalizeProjectPeriod(project: any): any {
        const next = { ...(project || {}) };
        const start = this.parseProjectDate(next.start_date);
        const end = this.parseProjectDate(next.end_date);
        if (!start || !end) {
            next.period_status = next.period_status || '-';
            next.status_color = next.status_color || 'none';
            return next;
        }

        const nowValue = new Date();
        const today = new Date(nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()).getTime();
        if (today < start.getTime()) {
            next.period_status = '예정';
            next.status_color = 'planned';
        } else if (today <= end.getTime()) {
            next.period_status = '진행중';
            next.status_color = 'ongoing';
        } else {
            next.period_status = '완료';
            next.status_color = 'completed';
        }
        return next;
    }

    private parseProjectDate(value: any): Date | null {
        if (!value) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).slice(0, 10));
        if (!match) return null;
        const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private async recordDamagePhotos(projectId: string, drawingId: string, damages: any[]): Promise<void> {
        if (!Array.isArray(damages)) return;
        for (const damage of damages) {
            const photos = this.normalizePhotoList(damage?.photos || []);
            const ownerId = damage?.id || damage?.number || drawingId;
            await this.recordPhotos(projectId, 'damage_photo', ownerId, photos, '손상 사진', drawingId);
        }
    }

    private async recordChecklistPhotos(projectId: string, responseId: string, answers: any): Promise<void> {
        if (!answers || typeof answers !== 'object') return;
        for (const itemId of Object.keys(answers)) {
            const photos = this.normalizePhotoList(answers[itemId]?.photos || []);
            await this.recordPhotos(projectId, 'checklist_photo', `${responseId}:${itemId}`, photos, '체크리스트 사진', responseId);
        }
    }

    private async recordChecklistItems(projectId: string, responseId: string, sections: any[]): Promise<void> {
        if (!Array.isArray(sections)) return;
        for (const section of sections) {
            const items = Array.isArray(section?.items) ? section.items : [];
            for (const item of items) {
                if (!item || !item.id) continue;
                await this.markCached(
                    projectId,
                    'checklist_item',
                    `checklist_item:${responseId}:${item.id}`,
                    item.label || `체크리스트 항목 ${item.id}`,
                    item,
                    { ref_id: item.id, parent_id: responseId }
                );
            }
        }
    }

    private async recordPhotos(
        projectId: string,
        category: string,
        ownerId: string,
        photos: any[],
        labelPrefix: string,
        parentId: string = ''
    ): Promise<void> {
        if (!Array.isArray(photos)) return;
        for (let index = 0; index < photos.length; index++) {
            const photo = photos[index];
            const photoId = photo.id || `${ownerId}:${index + 1}`;
            const label = photo.locationName || photo.name || `${labelPrefix} ${index + 1}`;
            const size = this.dataUrlSize(photo.data);
            if (photo.data && size > 0) {
                await this.markCached(projectId, category, `${category}:${ownerId}:${photoId}`, label, photo, {
                    ref_id: photoId,
                    parent_id: parentId || ownerId,
                    filename: photo.name || label,
                    size
                });
            } else {
                await this.markMissing(projectId, category, `${category}:${ownerId}:${photoId}`, label, '사진 데이터가 비어 있습니다.', {
                    ref_id: photoId,
                    parent_id: parentId || ownerId,
                    filename: photo.name || label
                });
            }
        }
    }

    private async recordChecklistExportAvailability(projectId: string, responseId: string, checklist: any, label: string): Promise<void> {
        const kind = checklist?.form_type === 'sports' ? 'xlsx' : 'hwpx';
        const cacheId = `checklist_export_${kind}:${responseId}`;
        const cached = await this.storage.get('files', cacheId);
        const exportLabel = `${label} 마지막 ${kind.toUpperCase()} 추출본`;
        if (cached && cached.blob) {
            await this.markCached(projectId, 'export', `export:${cacheId}`, exportLabel, cached, {
                required: false,
                ref_id: responseId,
                filename: cached.filename || exportLabel,
                size: cached.size || this.byteSize(cached.blob)
            });
        } else {
            await this.markMissing(projectId, 'export', `export:${cacheId}`, exportLabel, '온라인에서 한 번 추출해야 오프라인 export를 사용할 수 있습니다.', {
                required: false,
                ref_id: responseId,
                filename: exportLabel
            });
        }
    }

    private async recordInspectionExportAvailability(projectId: string, facilityId: string, facilityName: string): Promise<void> {
        const cacheId = `inspection_export_zip:${projectId}:${facilityId}`;
        const cached = await this.storage.get('files', cacheId);
        const label = `${facilityName} 마지막 도면 ZIP 추출본`;
        if (cached && cached.blob) {
            await this.markCached(projectId, 'export', `export:${cacheId}`, label, cached, {
                required: false,
                ref_id: facilityId,
                filename: cached.filename || label,
                size: cached.size || this.byteSize(cached.blob)
            });
        } else {
            await this.markMissing(projectId, 'export', `export:${cacheId}`, label, '온라인에서 한 번 추출해야 오프라인 export를 사용할 수 있습니다.', {
                required: false,
                ref_id: facilityId,
                filename: label
            });
        }
    }

    private async markCached(projectId: string, category: string, id: string, label: string, value: any, extra: Partial<OfflineCacheManifestItem> = {}): Promise<void> {
        await this.upsertManifestItem(projectId, {
            id,
            category,
            label,
            status: 'cached',
            size: extra.size !== undefined ? Number(extra.size || 0) : this.byteSize(value),
            cached_at: Date.now(),
            ...extra
        });
    }

    private async markFailed(projectId: string, category: string, id: string, label: string, error: any, extra: Partial<OfflineCacheManifestItem> = {}): Promise<void> {
        await this.upsertManifestItem(projectId, {
            id,
            category,
            label,
            status: 'failed',
            error: this.errorMessage(error),
            ...extra
        });
    }

    private async markMissing(projectId: string, category: string, id: string, label: string, message: string, extra: Partial<OfflineCacheManifestItem> = {}): Promise<void> {
        await this.upsertManifestItem(projectId, {
            id,
            category,
            label,
            status: 'missing',
            error: message,
            ...extra
        });
    }

    private async upsertManifestItem(projectId: string, item: OfflineCacheManifestItem): Promise<void> {
        await this.storage.updateCacheManifestItem(projectId, {
            ...item,
            updated_at: Date.now()
        });
    }

    private async cachePdfInServiceWorker(url: string, arrayBuffer: ArrayBuffer): Promise<void> {
        try {
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'CACHE_PDF',
                    url,
                    data: arrayBuffer
                });
            }
        } catch (error) {
            console.warn('[Prefetch] PDF CacheStorage write failed:', error);
        }
    }

    private normalizePhotoList(photos: any): any[] {
        return Array.isArray(photos)
            ? photos.filter((photo: any) => photo && typeof photo === 'object')
            : [];
    }

    private dataUrlSize(value: any): number {
        const data = String(value || '');
        if (!data) return 0;
        const commaIndex = data.indexOf(',');
        if (data.startsWith('data:') && commaIndex >= 0) {
            return Math.floor(data.slice(commaIndex + 1).length * 0.75);
        }
        return this.byteSize(data);
    }

    private byteSize(value: any): number {
        if (!value) return 0;
        if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size;
        if (value instanceof ArrayBuffer) return value.byteLength;
        if (ArrayBuffer.isView(value)) return value.byteLength;
        if (typeof value === 'string') {
            if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).byteLength;
            return value.length;
        }
        try {
            const json = JSON.stringify(value);
            if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).byteLength;
            return json.length;
        } catch {
            return 0;
        }
    }

    private errorMessage(error: any): string {
        if (!error) return '알 수 없는 오류';
        if (typeof error === 'string') return error;
        return error.message || error.statusText || '알 수 없는 오류';
    }

    private apiCall(namespace: string, funcName: string, data: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const url = `${this._baseuri}/api/${namespace}/${funcName}`;
            $.ajax({
                url,
                type: 'POST',
                data,
                crossDomain: /^https?:\/\//.test(url),
                xhrFields: {
                    withCredentials: true
                },
                success: (res: any) => resolve(res),
                error: (xhr: any, status: string, error: string) => reject(new Error(`${status}: ${error || xhr?.status || ''}`))
            });
        });
    }
}

let _prefetchInstance: PrefetchService | null = null;

export function getPrefetchService(): PrefetchService {
    if (!_prefetchInstance) {
        _prefetchInstance = new PrefetchService();
    }
    return _prefetchInstance;
}
